import { usePostOrder } from '@api/pos/pos-transaction-api.rtk';
import { UseDisclosureInterface } from '@hooks/useDisclosure';
import useToast from '@hooks/useToast';
import {
  selectPOSstationID,
  selectPOSTableDetails,
  selectPOSTableOrders,
  selectPOSTableTransactionID,
} from '@modules/point-of-sale/slice/pos-selector';
import { Info, SwapHoriz, SyncOutlined } from '@mui/icons-material';
import {
  Button,
  Chip,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { useAppSelector } from '@store/index';
import { selectPropertyID } from '@store/selector/property.selector';
import { parseNumber } from '@utils/format';
import { buildPosOrderPayload } from '@utils/pos-utils';
import { usePOSScreen } from '@utils/screen-size';
import { useMemo } from 'react';
import { getColor } from '../../../../../assets/theme';
import { usePOSOrderDetailsCtx } from './pos-order-taking-window';

/**
 * Props for the POSOrderHeader component.
 *
 * @typedef {Object} POSOrderHeaderProps
 * @property {UseDisclosureInterface} tableDrawerDisclosure - Disclosure state for the table selection drawer.
 * @property {UseDisclosureInterface} tableDetailsDrawerDisclosure - Disclosure state for the table details drawer.
 */

/**
 * POSOrderHeader component displays the header of the order-taking window in the POS system.
 * It shows the selected table, order details, and provides options to view table details and change tables.
 *
 * @param {POSOrderHeaderProps} props - The props for the component.
 * @returns {JSX.Element} The rendered component.
 */
const POSOrderHeader = ({
  tableDrawerDisclosure,
  tableDetailsDrawerDisclosure,
  loadingWindow,
}: {
  tableDrawerDisclosure: UseDisclosureInterface;
  tableDetailsDrawerDisclosure: UseDisclosureInterface;
  loadingWindow: UseDisclosureInterface;
}) => {
  const { toast } = useToast();
  const { isBillOut } = usePOSOrderDetailsCtx();
  const transactionDetails = useAppSelector(selectPOSTableDetails);
  const orders = useAppSelector(selectPOSTableOrders);
  const transactionID = useAppSelector(selectPOSTableTransactionID);
  const propertyID = useAppSelector(selectPropertyID);
  const stationID = useAppSelector(selectPOSstationID);
  const isPOS1024p = usePOSScreen();

  const { lightGray, primary, success, danger } = getColor();

  // Calculate total items in the order
  const totalItems = useMemo(
    () =>
      orders?.reduce(
        (prev, cur) => prev + (parseNumber(cur?.quantity?.toString()) || 0),
        0,
      ),
    [orders],
  );

  const isHaveNewItems = useMemo(
    () => orders?.some((item) => !Boolean(item.isPrintedOS) && !item.orderID),
    [orders],
  );

  // Check if the service type is dine-in
  const isDineIn = useMemo(
    () => transactionDetails?.serviceType?.toLowerCase()?.includes('dine'),
    [transactionDetails?.serviceType],
  );

  const [postOrder] = usePostOrder();

  const handleSyncOrder = async () => {
    try {
      loadingWindow.onOpen();
      await postOrder({
        transactionID,
        payload: {
          properties: [
            {
              property: {
                propertyID,
                posOrders: buildPosOrderPayload({
                  orders:
                    orders?.filter(
                      (item) => !item.isPrintedOS && !item.orderID,
                    ) ?? [],
                  propertyID,
                  stationID,
                }),
              },
            },
          ],
        },
      }).unwrap();
    } catch (error) {
      toast({ message: error, severity: 'warning' });
    } finally {
      loadingWindow.onClose();
    }
  };

  return (
    <Stack gap={1} sx={{ p: isBillOut ? 0 : 2 }}>
      <Stack gap={1}>
        {/* TITLE */}
        <Stack
          direction='row'
          alignItems='center'
          justifyContent='space-between'
          gap={1.5}
          flexWrap={'wrap'}
        >
          <Stack direction='row' alignItems='center' gap={1.5}>
            <IconButton
              size={isPOS1024p ? 'small' : 'medium'}
              sx={{
                bgcolor: lightGray,
                ':hover': {
                  bgcolor: primary,
                  color: '#FFF',
                },
              }}
              disabled={isBillOut}
              onClick={tableDetailsDrawerDisclosure.onOpen}
            >
              <Info sx={isPOS1024p ? { fontSize: '16px' } : {}} />
            </IconButton>
            <Typography variant='h4' fontWeight={600} noWrap>
              {transactionDetails?.tableAcr || ''}
            </Typography>
          </Stack>
          {/* TABLE DETAILS */}
          {!isBillOut && (
            <Stack
              direction='row'
              alignItems='center'
              gap={1}
              sx={{ flexWrap: 'wrap' }}
            >
              {transactionDetails?.serviceType && (
                <Chip
                  size={isPOS1024p ? 'small' : 'medium'}
                  label={transactionDetails?.serviceType}
                  sx={{
                    fontSize: '12px',
                    bgcolor: isDineIn ? success : danger,
                    color: '#FFF',
                    px: 0.7,
                    borderRadius: '100px',
                  }}
                />
              )}

              <Button
                variant='contained'
                size='small'
                startIcon={<SwapHoriz />}
                sx={{
                  borderRadius: '48px',
                  whiteSpace: 'nowrap',
                  fontSize: '12px',
                  py: isPOS1024p ? 0.3 : '5.5px',
                  bgcolor: '#DFE3E8',
                  color: '#000',
                  boxShadow: 1,
                }}
                onClick={tableDrawerDisclosure.onOpen}
              >
                Table Select
              </Button>

              {isHaveNewItems && (
                <Tooltip title='Sync Order(s)' placement='top'>
                  <IconButton
                    size='small'
                    sx={{
                      bgcolor: '#DFE3E8',
                      color: '#000',
                      ':hover': {
                        bgcolor: '#bbbfdc',
                      },
                      boxShadow: 1,
                    }}
                    disableRipple
                    onClick={handleSyncOrder}
                  >
                    <SyncOutlined sx={isPOS1024p ? { fontSize: '16px' } : {}} />
                  </IconButton>
                </Tooltip>
              )}
            </Stack>
          )}

          {/* SETTINGS */}
          {/* {!isBillOut && (
            <IconButton
              size={isPOS1024p ? 'small' : 'medium'}
              sx={{
                bgcolor: lightGray,
                ':hover': {
                  bgcolor: primary,
                  color: '#FFF',
                },
              }}
              disabled
            >
              <Settings
                fontSize='inherit'
                sx={isPOS1024p ? { fontSize: '16px' } : {}}
              />
            </IconButton>
          )} */}
        </Stack>

        {/* ORDER DETAILS */}
        <Stack
          direction='row'
          alignItems='center'
          gap={1}
          sx={{ pb: isBillOut ? 2 : 0, flexWrap: 'wrap' }}
        >
          {useMemo(() => {
            return ['Trans No.: ', 'Pax: ', 'Item: '].map((item, i) => {
              let newItem = item;

              if (i === 2) {
                newItem += totalItems;
              } else if (i === 1) {
                const totalPax = transactionDetails?.totalPax ?? 0;
                newItem += totalPax;
              } else if (i === 0) {
                newItem += transactionDetails?.transNo ?? '';
              }

              return (
                <Chip
                  key={i}
                  size={isPOS1024p ? 'small' : 'medium'}
                  variant='filled'
                  sx={{ bgcolor: '#161c2414', cursor: 'default' }}
                  label={newItem}
                />
              );
            });
          }, [totalItems, transactionDetails])}
        </Stack>
      </Stack>
    </Stack>
  );
};

export default POSOrderHeader;
